import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SmartBookingLayout } from '../components/SmartBookingLayout';
import { StepCard } from '../components/StepCard';
import { SyncLoader } from 'react-spinners';
import { RootState } from '../redux/store';
import {
  setPatientDetails,
  setRequestMetadata,
  setTreatment,
  setPatientType,
  setPrimetimeFilter,
  setReferral,
  resetAppointment,
  createEmptyPatientDetails
} from '../redux/slices/appointmentSlice';
import { Button } from '../components/Button';
import { StepActions } from '../components/StepActions';
import Toggle from '../components/Toggle';
import { searchPatientDetails, createPatient, fetchReferral, getUserFacingErrorMessage } from '../services/api';
import { PATIENT_TYPE_TO_BACKEND, PatientTypeEnum } from '../types';
import { AvailableSlots } from '../components/AvailableSlots';
import PreferredTimeSlots from '../components/PreferredTimeSlots';
import { useReferralToken } from '../hooks/useReferralToken';

const ERROR_MESSAGES = {
  INVALID_APPOINTMENT_TYPE: 'Invalid appointment_type. Please check the link and try again.',
  PATIENT_LOAD_FAILED: 'Failed to load patient information',
  PATIENT_SAVE_FAILED: 'Failed to save patient. Please try again.',
  CONTACT_SUPPORT: 'Please contact support if you need assistance.',
  REFERRAL_NOT_FOUND: "We couldn't find this referral. Please check the link from your text message.",
  REFERRAL_EXPIRED: 'This referral link has expired.',
  REFERRAL_NOT_BOOKABLE: "This referral can't be booked online.",
} as const;

// Dead-end referral errors: the patient can't fix the link, so point them to the practice
const withCallUs = (message: string, phone?: string | null) =>
  phone ? `${message} Please call us at ${phone}.` : message;

export const SmartBooking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { practice, token } = useSelector((s: RootState) => s.auth);
  const { patientType, selectedSlot, patientDetails, request_id, primetimeFilter, referral } = useSelector(
    (s: RootState) => s.appointment
  );

  const [loading, setLoading] = useState(true);
  const [savingPatient, setSavingPatient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [alreadyBooked, setAlreadyBooked] = useState(false);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const appointmentTypeSlug = params.get('appointment_type');
  const patientId = params.get('patient_id');
  const referralToken = useReferralToken();
  const isPracticeHasManualScheduler = practice?.scheduler_type === 'manual';


  // Initialize appointment type and patient details
  useEffect(() => {
    const initialize = async () => {
      if (!token || !practice) {
        setLoading(false);
        return;
      }

      if (isPracticeHasManualScheduler && !referralToken) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setAlreadyBooked(false);

      try {
        if (referralToken) {
          await loadReferral(referralToken);
          return;
        }
        if (referral) dispatch(resetAppointment());

        // Load patient details first: setting the patient type clears any selected treatment.
        if (patientId) {
          await loadPatientDetails(patientId);
        }

        // Validate and set appointment type
        if (appointmentTypeSlug) {
          const matchedAppointmentType = practice.appointment_types?.find(
            (at) => at.slug?.toLowerCase() === appointmentTypeSlug.toLowerCase()
          );

          if (!matchedAppointmentType) {
            setError(ERROR_MESSAGES.INVALID_APPOINTMENT_TYPE);
            return;
          }

          dispatch(setTreatment(matchedAppointmentType));
        }
      } catch (e: any) {
        setError(e?.message || ERROR_MESSAGES.PATIENT_LOAD_FAILED);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [token, practice, appointmentTypeSlug, patientId, referralToken]);

  // Prefill from a specialist referral the partner office sent to the patient
  const loadReferral = async (referralTokenValue: string) => {
    let response;
    try {
      response = await fetchReferral(referralTokenValue);
    } catch (e: any) {
      const status = e?.response?.status;
      const phone = e?.response?.data?.phone_number;
      if (status === 410) {
        throw new Error(withCallUs(ERROR_MESSAGES.REFERRAL_EXPIRED, phone));
      }
      throw new Error(ERROR_MESSAGES.REFERRAL_NOT_FOUND);
    }
    const attrs = response.data.attributes;

    // A different referral must not inherit the previous patient's id or external_resource_id
    if (referral?.token !== referralTokenValue) dispatch(resetAppointment());
    dispatch(setReferral({
      token: referralTokenValue,
      partnerTitle: attrs.partner_title,
      partnerLogo: attrs.partner_logo,
      referringDoctor: attrs.referring_doctor,
      reasonLabel: attrs.reason_label,
    }));

    dispatch(setPatientType(PatientTypeEnum.New));
    dispatch(setPatientDetails({
      ...createEmptyPatientDetails(),
      firstName: attrs.first_name || '',
      lastName: attrs.last_name || '',
      email: attrs.email || '',
      phone: attrs.phone_number || '',
      birth_date: attrs.birth_date || '',
    }));

    if (attrs.booked) {
      setAlreadyBooked(true);
      return;
    }

    // The type comes from the referral itself, not the link; a miss means it's no longer bookable online
    const matchedAppointmentType = practice?.appointment_types?.find((at) => at.id === attrs.appointment_type_id);
    if (!matchedAppointmentType) throw new Error(withCallUs(ERROR_MESSAGES.REFERRAL_NOT_BOOKABLE, practice?.phone_number));

    dispatch(setTreatment(matchedAppointmentType));
  };

  // Load patient details
  const loadPatientDetails = async (encryptedId: string) => {
    const response = await searchPatientDetails({ patient_encrypted_id: encryptedId });
    const patientData = response.data.attributes;

    dispatch(setPatientType(PatientTypeEnum.Existing));
    dispatch(setPatientDetails({
      firstName: patientData.first_name || '',
      lastName: patientData.last_name || '',
      email: patientData.email || '',
      phone: patientData.phone_number || '',
      id: response.data.id,
      external_resource_id: patientData.external_resource_id,
      notes: '',
    }));
  };

  // Save patient and proceed
  const savePatient = async () => {
    if (isPracticeHasManualScheduler) {
      if (!isFormValid) return;
    } else {
      if (!selectedSlot) return;
    }

    if (request_id) {
      const paramsClone = new URLSearchParams(location.search);
      paramsClone.set('smart_booking', 'true');
      navigate(`../confirmation?${paramsClone.toString()}`);
      return;
    }

    setSavingPatient(true);
    try {
      const payload = {
        first_name: patientDetails.firstName,
        last_name: patientDetails.lastName,
        email: patientDetails.email,
        phone_number: patientDetails.phone,
        patient_type: PATIENT_TYPE_TO_BACKEND[patientType as PatientTypeEnum],
        patient_id: patientDetails.id,
        external_resource_id: patientDetails.external_resource_id,
        ...(referralToken ? { birth_date: patientDetails.birth_date } : {}),
      };

      const response = await createPatient(payload, referralToken);
      const data = response.data;
      const attributes = data.attributes;
      dispatch(setRequestMetadata({
        request_id: data.id,
        requestable_id: attributes.requestable_id,
        requestable_type: attributes.requestable_type,
      }));
      // Navigate to confirmation
      const paramsClone = new URLSearchParams(location.search);
      paramsClone.set('smart_booking', 'true');
      navigate(`../confirmation?${paramsClone.toString()}`);
    } catch (error: any) {
      setError(getUserFacingErrorMessage(error, ERROR_MESSAGES.PATIENT_SAVE_FAILED));
    } finally {
      setSavingPatient(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <SmartBookingLayout>
        <div className="flex items-center justify-center min-h-[40vh]">
          <SyncLoader />
        </div>
      </SmartBookingLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <SmartBookingLayout>
        <StepCard>
          <div className="flex items-center justify-center min-h-[40vh] text-center">
            <div>
              <p className="text-lg font-medium text-red-600 mb-2">{error}</p>
              <p className="text-sm text-gray-500">{ERROR_MESSAGES.CONTACT_SUPPORT}</p>
            </div>
          </div>
        </StepCard>
      </SmartBookingLayout>
    );
  }

  if (alreadyBooked) {
    return (
      <SmartBookingLayout>
        <StepCard>
          <div className="flex items-center justify-center min-h-[40vh] text-center">
            <div>
              <p className="text-lg font-medium text-black mb-2">You're already booked</p>
              <p className="text-sm text-gray-500">
                {practice?.title} has your appointment request{practice?.phone_number ? `. Questions? Call ${practice.phone_number}.` : '.'}
              </p>
            </div>
          </div>
        </StepCard>
      </SmartBookingLayout>
    );
  }

  return (
    <SmartBookingLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-black">
              {referral ? `Hi ${patientDetails.firstName}, pick a time` : 'When would you like to come?'}
            </h1>
            <p className="text-sm text-gray-500">
              {referral
                ? `${referral.referringDoctor ? `${referral.referringDoctor} at ` : ''}${referral.partnerTitle} referred you to ${practice?.title}${referral.reasonLabel ? ` for a ${referral.reasonLabel}` : ''}.`
                : 'Convenient evenings and weekends to fit your schedule'}
            </p>
          </div>
          {practice?.promote_primetime && (
            <div className="pr-2">
              <Toggle
                selected={primetimeFilter}
                onChange={(value) => dispatch(setPrimetimeFilter(value))}
              />
            </div>
          )}
        </div>

        {isPracticeHasManualScheduler ? (
          <PreferredTimeSlots onValidationChange={setIsFormValid} />
        ) : (
          <AvailableSlots />
        )}

        <StepActions className="justify-end">
          <Button
            title={savingPatient ? 'Saving...' : 'Continue'}
            disabled={(isPracticeHasManualScheduler ? !isFormValid : !selectedSlot) || savingPatient}
            onClick={savePatient}
          />
        </StepActions>
      </div>
    </SmartBookingLayout>
  );
};
